import { isMatching, P } from 'ts-pattern';
import { z } from 'zod';
import api from '~/utils/api';
import { isAppEmbedded } from '~/utils/appEmbed';
import { computeCost, sumBy } from '~/utils/compute';
import { RollupStatusCode } from '~/utils/constants';
import { createCurrencyFormatter, formatDate } from '~/utils/formatters';
import { makeLoader, redirect } from '~/utils/routing';
import { AsyncReturnType } from '~/utils/types';

type Claim = AsyncReturnType<typeof api.fetchCrewClaim>;
type Shipment = NonNullable<Claim['claimReturnShipments']>[number];
type ClaimLineItem = Claim['claimLineItems'][number];

const formatResolutionSummary = (claim: Claim) => {
  // TODO revisit types, as these may no longer actually be optional
  const {
    refunds = [],
    giftCards = [],
    exchangeOrders = [],
  } = claim.resolutionSummary ?? {};

  return (
    claim.resolutionSummary && {
      ...(exchangeOrders.length && { exchangeOrders }),

      ...(refunds.length && {
        refundsTotal: {
          amount: sumBy(({ amount }: { amount: number }) => amount)(refunds),
          handlingFee: sumBy(
            ({ handlingFee }: { handlingFee: number }) => handlingFee,
          )(refunds),
          shippingFee: sumBy(
            ({ shippingFee }: { shippingFee: number }) => shippingFee,
          )(refunds),
        },
      }),

      ...(giftCards.length && {
        giftCardsTotal: {
          amount: sumBy(({ amount }: { amount: number }) => amount)(giftCards),
          incentiveAmount: sumBy(
            ({ incentiveAmount }: { incentiveAmount: number }) =>
              incentiveAmount,
          )(giftCards),
        },
      }),
    }
  );
};

const getReturnByDate = ({
  shipment,
  isInstantExchange,
  customerPayment,
}: {
  shipment: Shipment;
  isInstantExchange: boolean;
  customerPayment: Claim['customerPayment'];
}) => {
  const paymentExpiresOn =
    customerPayment?.authorizationExpiresOn ?? customerPayment?.capturedOn;

  return isInstantExchange && paymentExpiresOn ? paymentExpiresOn : (
      shipment.shipmentExpiresOn
    );
};

const getReturnByText = (returnByDate: string, isExpired: boolean) =>
  `${isExpired ? 'Return Expired On' : 'Return Items By'}: ${formatDate(
    returnByDate,
  )}`;

const getReturnShipmentHeadline = ({
  shipmentInTransit,
  status,
  returnByDate,
  isReturnByDateExpired,
}: {
  shipmentInTransit: boolean;
  status: Claim['claimRollup']['code'];
  returnByDate: string;
  isReturnByDateExpired: boolean;
}) => {
  if (shipmentInTransit) {
    return 'Items you returned';
  }

  if (returnByDate) {
    return getReturnByText(returnByDate, isReturnByDateExpired);
  }

  const statusHeadline = {
    [RollupStatusCode.inProgress]: 'Next Steps',
    [RollupStatusCode.readyForReview]: 'We are reviewing your information',
    [RollupStatusCode.completed]: 'We have reviewed your request',
  } satisfies Record<RollupStatusCode, string>;

  return statusHeadline[status];
};

const getReturnShipmentTypeText = (shipment: Shipment) => {
  if (shipment.returnShipmentType === 'Label' && shipment.qrCodeDetail) {
    return 'QR Code';
  }

  if (shipment.returnShipmentType === 'Packing_Slip') {
    return 'packing slip';
  }

  return 'label';
};

const getShipmentNotInTransitText = (
  shipment: Shipment,
  isInstantExchange: boolean,
) =>
  `Please use the provided ${getReturnShipmentTypeText(
    shipment,
  )} to return your items${
    isInstantExchange ?
      '. Because you selected an instant exchange, your new order will be processed immediately.'
    : ''
  }`;

const getReturnShipmentTitle = ({
  claim: {
    isInstantExchange,
    claimRollup: { code: status },
  },
  shipment,
  isExpired,
}: {
  claim: Claim;
  shipment: Shipment;
  isExpired: boolean;
}) => {
  if (isExpired && status !== 'Completed') {
    return 'Your return has expired';
  }

  if (!shipment.inTransitOn) {
    return getShipmentNotInTransitText(shipment, isInstantExchange);
  }

  const statusTitles = {
    [RollupStatusCode.inProgress]: `Your return is in progress`,
    [RollupStatusCode.readyForReview]: 'Your return is in-review',
    [RollupStatusCode.completed]: 'Your return is complete',
  } satisfies Record<RollupStatusCode, string>;

  return statusTitles[status];
};

const addCostToLineItem = (lineItem: ClaimLineItem) => ({
  ...lineItem,
  cost: computeCost({
    unitPrice: lineItem.originalStoreOrderLineItem.unitPrice, // ? should this be the unitPrice from the claimLineItem?
    quantity: lineItem.quantity,
  }),
});

const downloadLabels = {
  Label: 'Download Return Label',
  Packing_Slip: 'Download Packing Slip',
} satisfies Record<Shipment['returnShipmentType'], string>;

const formatShipment = (claim: Claim) => (shipment: Shipment) => {
  // we only show "return shipments" here
  // shipments where the customer is returning the items
  if (!shipment.isReturnShipment) return null;

  const returnByDate = getReturnByDate({
    shipment,
    isInstantExchange: claim.isInstantExchange,
    customerPayment: claim.customerPayment,
  });
  const isExpired =
    !!returnByDate && Date.now() >= new Date(returnByDate).getTime();

  return {
    ...shipment,
    returnByDate,
    isExpired,
    headline: getReturnShipmentHeadline({
      shipmentInTransit: !!shipment.inTransitOn,
      status: claim.claimRollup.code,
      returnByDate,
      isReturnByDateExpired: isExpired,
    }),
    title: getReturnShipmentTitle({
      claim,
      shipment,
      isExpired,
    }),
    claimLineItems: shipment.claimLineItems.map(addCostToLineItem),
    downloadLabel: downloadLabels[shipment.returnShipmentType],
  };
};

const formatRequestSummary = (claim: Claim) => {
  const replacementOrderLineItems =
    claim.claimLineItems
      .find((li) => li.resolutionMethodEnum === 'Replacement_Order')
      ?.replacementItems?.map((ri) => ri)
      .filter(Boolean) ?? [];

  const variantExchangeLineItems = claim.claimLineItems
    .filter((li) => li.resolutionMethodEnum === 'Variant_Exchange')
    .map((lineItem) => lineItem.variantExchangeLineItem)
    .filter(Boolean)
    .concat(replacementOrderLineItems);

  const refundTotal = claim.requestSummary?.totals?.refund ?? 0;
  const giftCardTotal = claim.requestSummary?.totals?.giftCard ?? 0;

  if (
    !variantExchangeLineItems.length &&
    refundTotal <= 0 &&
    giftCardTotal <= 0
  )
    return undefined;

  const currencyFormatter = createCurrencyFormatter(
    claim.originalStoreOrder.currencyCode,
  );
  const isPositive = isMatching(P.number.positive());

  const showGiftCardChips = isPositive(giftCardTotal);
  const showRefundChips = isPositive(refundTotal) && !showGiftCardChips;

  const chips = [];

  if (isPositive(claim.giftCardIncentiveAmountApplied) && showGiftCardChips) {
    chips.push(
      `Incentive: +${currencyFormatter(claim.giftCardIncentiveAmountApplied)}`,
    );
  }

  if (isPositive(claim.feeApplied)) {
    chips.push(
      `${claim.feeName ?? 'Handling Fee'}: -${currencyFormatter(claim.feeApplied)}`,
    );
  }

  if (isPositive(claim.shopNowIncentiveAmountApplied)) {
    chips.push(
      `Shop Now Incentive: +${currencyFormatter(claim.shopNowIncentiveAmountApplied)}`,
    );
  }

  if (isPositive(claim.returnShippingAmountApplied)) {
    chips.push(
      `Return Shipping: -${currencyFormatter(claim.returnShippingAmountApplied)}`,
    );
  }

  if (isPositive(claim.exchangeOrderShippingAmountApplied)) {
    chips.push(
      `Exchange Shipping: -${currencyFormatter(claim.exchangeOrderShippingAmountApplied)}`,
    );
  }

  return {
    giftCardChips: showGiftCardChips ? chips : [],
    refundChips: showRefundChips ? chips : [],
    variantExchangeLineItems,
    refundTotal,
    giftCardTotal,
  };
};

type Settings = AsyncReturnType<typeof api.fetchSettings>;
const getGreenReturns = (claim: Claim, theme: Settings['theme']) => {
  const returnItemIds = new Set(
    (claim.claimReturnShipments ?? []).flatMap((shipment) =>
      shipment.claimLineItems.map((lineItem) => lineItem.id),
    ),
  );

  return {
    title:
      claim.claimType === 'Return' ?
        (theme.noReturnRequiredItemsDetailText ??
        'You do not need to return these items.')
      : (theme.warrantyItemsDetailText ??
        'We will be in touch soon regarding your warranty claim.'),
    headline:
      claim.claimType === 'Return' ?
        (theme.noReturnRequiredItemsHeadline ?? 'Items not required to return')
      : 'Items submitted',
    items: claim.claimLineItems
      .filter((lineItem) => !returnItemIds.has(lineItem.id))
      .map(addCostToLineItem),
  };
};

const paramsSchema = z.object({
  claimExternalId: z.string(),
  store: z.string().default(''),
});

export const { loader, useLoaderData: useClaimOverviewData } = makeLoader(
  ({ params }, appStore) => {
    const { settings } = appStore.getState();
    const { claimExternalId, store } = paramsSchema.parse(params);

    if (!settings) {
      return redirect(`/${store}`);
    }

    return api
      .fetchCrewClaim({
        params: {
          storefrontId: settings.storefrontId,
          claimExternalId,
        },
      })
      .then((claim) => ({
        storeUrl: settings.storeUrl,
        currencyCode: claim.originalStoreOrder.currencyCode,
        claimExternalId: claim.externalId,
        claimRollup: claim.claimRollup,
        noteToCustomer: claim.noteToCustomer,
        greenReturns: getGreenReturns(claim, settings.theme),
        resolutionSummary: formatResolutionSummary(claim),
        requestSummary: formatRequestSummary(claim),
        hideLabelDownloadWhenQrCodeIsPresent:
          settings.theme.hideLabelDownloadWhenQrCodeIsPresent ?? false,
        isCompleted: claim.claimRollup.code === RollupStatusCode.completed,
        isEmbedded: isAppEmbedded(),
        returnShipments: (claim.claimReturnShipments ?? [])
          .map(formatShipment(claim))
          .filter(Boolean)
          .sort((a, b) => (a.createdOn > b.createdOn ? -1 : 1)),
      }))
      .catch(() => redirect(`/${store}`));
  },
);
